/**
 * Cross-Platform Storage Service
 *
 * On native (iOS/Android): uses capacitor-secure-storage-plugin for sensitive data
 * (Keychain on iOS, EncryptedSharedPreferences on Android).
 * On web: uses localStorage (unchanged behavior).
 *
 * The service auto-detects the platform and uses the appropriate backend.
 * Call `initSecureStorage()` at app startup on native before any storage operations.
 */

import { Capacitor } from '@capacitor/core';

type SecureStoragePluginType = {
  get(options: { key: string }): Promise<{ value: string }>;
  set(options: { key: string; value: string }): Promise<void>;
  remove(options: { key: string }): Promise<void>;
  clear(): Promise<void>;
  keys(): Promise<{ value: string[] }>;
};

let _securePlugin: SecureStoragePluginType | null = null;
let _secureReady = false;

/**
 * Initialize the secure storage plugin on native platforms.
 * No-op on web. Safe to call multiple times.
 */
export async function initSecureStorage(): Promise<void> {
  if (_secureReady || !Capacitor.isNativePlatform()) {
    _secureReady = true;
    return;
  }

  try {
    const { SecureStoragePlugin } = await import('capacitor-secure-storage-plugin');
    _securePlugin = SecureStoragePlugin as unknown as SecureStoragePluginType;
    _secureReady = true;
  } catch (err) {
    console.warn('[Storage] SecureStoragePlugin not available, falling back to localStorage:', err);
    _secureReady = true;
  }
}

export const storage = {
  async get(key: string): Promise<string | null> {
    try {
      if (_securePlugin) {
        try {
          const result = await _securePlugin.get({ key });
          return result.value;
        } catch {
          // Key not found in secure storage
          return null;
        }
      }
      return localStorage.getItem(key);
    } catch (error) {
      console.error('[Storage] Error getting item:', error);
      return null;
    }
  },

  async set(key: string, value: string): Promise<void> {
    try {
      if (_securePlugin) {
        await _securePlugin.set({ key, value });
        return;
      }
      localStorage.setItem(key, value);
    } catch (error) {
      console.error('[Storage] Error setting item:', error);
    }
  },

  async remove(key: string): Promise<void> {
    try {
      if (_securePlugin) {
        try {
          await _securePlugin.remove({ key });
        } catch {
          // Key doesn't exist — ignore
        }
        return;
      }
      localStorage.removeItem(key);
    } catch (error) {
      console.error('[Storage] Error removing item:', error);
    }
  }
};
